import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { AUTH_CONFIG } from '../auth0-config';

import { IField, IDocument, IGroundTruth, ITokenized } from '../types/my-types';

import { getAxiosError } from '../utils/getAxiosError';

import {
  ApiError,
  IFieldCreateParams,
  IFieldListResponse,
  IFieldsListParams,
  IFieldUpdateParams,
  IDocumentCreateParams,
  IDocumentListResponse,
  IDocumentUpdateParams,
  IDocumentListParams,
  IGroundTruthCreateParams,
  IGroundTruthListParams,
  IGroundTruthListResponse,
  IGroundTruthUpdateParams,
  IWrapper,
  ITokenizedCreateParams,
  ITokenizedsListParams,
  ITokenizedListResponse,
  ITokenizedUpdateParams,
} from './types';

axios.defaults.baseURL = AUTH_CONFIG.apiUrl;
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.put['Content-Type'] = 'application/json';

const responseInterceptor = (response: AxiosResponse) => {
  return response.data;
};
const errorInterceptor = (error: AxiosError) => {
  if (error.isAxiosError) {
    const maintenance = sessionStorage.getItem('maintenance');
    if (error?.response?.status === 503 && !maintenance) {
      sessionStorage.setItem('maintenance', 'on');
    }
    // Preserve the HTTP status and structured error_code on the rejected
    // Error (additive — existing callers that only read `.message` are
    // unaffected) so callers can branch on specific API errors, e.g. the
    // document-split 409s (ALREADY_SPLIT).
    const enriched = new Error(getAxiosError(error)) as ApiError;
    enriched.status = error.response?.status;
    const data = error.response?.data as { error_code?: string; code?: string } | undefined;
    enriched.errorCode = data?.error_code ?? data?.code;
    enriched.responseData = error.response?.data;
    return Promise.reject(enriched);
  } else {
    return Promise.reject(new Error(error.message));
  }
};

class Wrapper implements IWrapper {
  client: AxiosInstance;
  dataPoolId: string;

  constructor(token: string, dataPoolId: string) {
    this.dataPoolId = dataPoolId;
    this.client = axios.create();
    this.client.interceptors.response.use(responseInterceptor, errorInterceptor);
    this.client.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
  // TODO: better to delegate the wrapper methods to individual model, otherwise this file can get huge quickly
  // we can add common or most used methods here

  /**
   * Creates a new field in your organization
   * @param {Object} data
   */
  createField(data: IFieldCreateParams) {
    return this.client.post<IField, IField>(
      `data-pools/${this.dataPoolId}/fields/?advanced=true&es=1`,
      data
    );
  }

  /**
   * Gets a list of fields
   * @param {Object} params
   */
  listFields(params: IFieldsListParams) {
    return this.client.get<IFieldListResponse, IFieldListResponse>(
      `data-pools/${this.dataPoolId}/fields/?es=1`,
      { params }
    );
  }

  /**
   * Gets a single field object
   * @param {string} id
   */
  retrieveField(id: string) {
    return this.client.get<IField, IField>(`data-pools/${this.dataPoolId}/fields/${id}/`);
  }

  /**
   * Updates a field
   * @param {string} id
   * @param {Object} data
   */
  updateField(id: string, data: IFieldUpdateParams) {
    return this.client.patch<IField, IField>(
      `data-pools/${this.dataPoolId}/fields/${id}/?advanced=true&es=1`,
      data
    );
  }

  /**
   * Permanently removes a field from your training set or extraction.
   * @param {string} id
   */
  removeField(id: string) {
    return this.client.delete(`data-pools/${this.dataPoolId}/fields/${id}/?advanced=true`);
  }

  /**
   * Creates a new document in your data pool
   * @param {Object} data
   */
  createDocument(data: IDocumentCreateParams) {
    return this.client.post<IDocument, IDocument>(
      `data-pools/${this.dataPoolId}/documents/?advanced=true`,
      data
    );
  }

  /**
   * Gets a list of documents
   * @param {Object} params
   */
  listDocuments(params: IDocumentListParams) {
    return this.client.get<IDocumentListResponse, IDocumentListResponse>(
      `data-pools/${this.dataPoolId}/documents/`,
      { params }
    );
  }

  /**
   * Gets a single document object
   * @param {string} id
   */
  retrieveDocument(id: string) {
    return this.client.get<IDocument, IDocument>(
      `data-pools/${this.dataPoolId}/documents/${id}/?advanced=true`
    );
  }

  /**
   * Updates a document
   * @param {string} id
   * @param {Object} data
   */
  updateDocument(id: string, data: IDocumentUpdateParams) {
    return this.client.patch<IDocument, IDocument>(
      `data-pools/${this.dataPoolId}/documents/${id}/?advanced=true&es=1`,
      data
    );
  }

  /**
   * Permanently removes a document from your data pool.
   * @param {string} id
   */
  removeDocument(id: string) {
    return this.client.delete(`data-pools/${this.dataPoolId}/documents/${id}/?advanced=true`);
  }

  /**
   * Creates a new ground truth in your organization
   * @param {Object} data
   */
  createGroundTruth(data: IGroundTruthCreateParams) {
    return this.client.post<IGroundTruth, IGroundTruth>(
      `data-pools/${this.dataPoolId}/ground-truths/?advanced=true`,
      data
    );
  }

  /**
   * Gets a list of ground truths
   * @param {Object} params
   */
  listGroundTruths(params: IGroundTruthListParams) {
    return this.client.get<IGroundTruthListResponse, IGroundTruthListResponse>(
      `data-pools/${this.dataPoolId}/ground-truths/`,
      { params }
    );
  }

  /**
   * Gets a single ground truth object
   * @param {string} id
   */
  retrieveGroundTruth(id: string) {
    return this.client.get<IGroundTruth, IGroundTruth>(
      `data-pools/${this.dataPoolId}/ground-truths/${id}/`
    );
  }

  /**
   * Updates a ground truth
   * @param {string} id
   * @param {Object} data
   */
  updateGroundTruth(id: string, data: IGroundTruthUpdateParams) {
    return this.client.patch<IGroundTruth, IGroundTruth>(
      `data-pools/${this.dataPoolId}/ground-truths/${id}/?advanced=true`,
      data
    );
  }

  /**
   * Permanently removes a ground truth from your training set or extraction.
   * @param {string} id
   */
  removeGroundTruth(id: string) {
    return this.client.delete(`data-pools/${this.dataPoolId}/ground-truths/${id}/?advanced=true`);
  }

  /**
   * Creates a new tokenized in your data pool
   * @param {Object} data
   */
  createTokenized(data: ITokenizedCreateParams) {
    return this.client.post<ITokenized, ITokenized>(
      `data-pools/${this.dataPoolId}/tokenizeds/?advanced=true`,
      data
    );
  }

  /**
   * Gets a list of tokenizeds
   * @param {Object} params
   */
  listTokenizeds(params: ITokenizedsListParams) {
    return this.client.get<ITokenizedListResponse, ITokenizedListResponse>(
      `data-pools/${this.dataPoolId}/tokenizeds/?advanced=true`,
      { params }
    );
  }

  /**
   * Gets a single tokenized object
   * @param {string} id
   */
  retrieveTokenized(id: string) {
    return this.client.get<ITokenized, ITokenized>(
      `data-pools/${this.dataPoolId}/tokenizeds/${id}/?advanced=true`
    );
  }

  /**
   * Gets a single tokenized object
   * @param {string} id
   */
  retrieveRefreshedTokenized(id: string) {
    return this.client.get<ITokenized, ITokenized>(
      `data-pools/${this.dataPoolId}/tokenizeds/${id}/?advanced=true&refresh=true`
    );
  }

  /**
   * Updates a tokenized
   * @param {string} id
   * @param {Object} data
   */
  updateTokenized(id: string, data: ITokenizedUpdateParams) {
    return this.client.patch<ITokenized, ITokenized>(
      `data-pools/${this.dataPoolId}/tokenizeds/${id}/?advanced=true&es=1`,
      data
    );
  }

  /**
   * Permanently removes a tokenized from your data pool.
   * @param {string} id
   */
  removeTokenized(id: string) {
    return this.client.delete(`data-pools/${this.dataPoolId}/tokenizeds/${id}/?advanced=true`);
  }
}

export default Wrapper;
